import type { LocationQueryRaw } from '#vue-router';
import {
    type CountryISO2,
    FILTER_COMPANION_SEATS_VALUES,
    FILTER_DISPLACEMENT_END,
    FILTER_DISPLACEMENT_START,
    FILTER_DISPLACEMENT_STEP,
    FILTER_FOLDING_SEATS_VALUES,
    FILTER_FUEL_TANK_CAPACITY_END,
    FILTER_FUEL_TANK_CAPACITY_START,
    FILTER_FUEL_TANK_CAPACITY_STEP,
    FILTER_HEIGHT_END,
    FILTER_HEIGHT_START,
    FILTER_HEIGHT_STEP,
    FILTER_KM_END,
    FILTER_KM_START,
    FILTER_KM_STEP,
    FILTER_LENGTH_END,
    FILTER_LENGTH_START,
    FILTER_LENGTH_STEP,
    FILTER_OPENABLE_WINDOWS_END,
    FILTER_OPENABLE_WINDOWS_START,
    FILTER_OPENABLE_WINDOWS_STEP,
    FILTER_POWER_END,
    FILTER_POWER_START,
    FILTER_POWER_STEP,
    FILTER_PRICE_END,
    FILTER_PRICE_START,
    FILTER_PRICE_STEP,
    FILTER_REGISTRATION_YEAR_END,
    FILTER_REGISTRATION_YEAR_START,
    FILTER_REGISTRATION_YEAR_STEP,
    FILTER_SEATS_VALUES,
    FILTER_STANDING_PLACES_END,
    FILTER_STANDING_PLACES_START,
    FILTER_STANDING_PLACES_STEP,
    FILTER_TOTAL_CAPACITY_END,
    FILTER_TOTAL_CAPACITY_START,
    FILTER_TOTAL_CAPACITY_STEP,
    FILTER_WHEELCHAIR_PLACES_VALUES,
    FILTER_WIDTH_END,
    FILTER_WIDTH_START,
    FILTER_WIDTH_STEP,
    isCountryISO2,
    range,
    SEARCH_MIN_QUERY_LENGTH,
} from '@lib/shared';
import { useQuery } from '@tanstack/vue-query';
import lodashFlatten from 'lodash-es/flatten';
import lodashIsEqual from 'lodash-es/isEqual';
import lodashIsNumber from 'lodash-es/isNumber';
import lodashIsString from 'lodash-es/isString';
import lodashMapKeys from 'lodash-es/mapKeys';
import lodashOmit from 'lodash-es/omit';
import lodashSnakeCase from 'lodash-es/snakeCase';
import lodashUniq from 'lodash-es/uniq';
import type { ComputedRef, WritableComputedRef } from 'vue';
import { computed, unref } from 'vue';

import {
    apiAuctionsListingBrands,
    apiAuctionsListingModels,
    type ApiAuctionsListingParams,
    apiDirectSalesListingBrands,
    apiDirectSalesListingModels,
    type ApiDirectSalesListingParams,
    GetAuctionStatesResponseDtoStatesItem,
    AssetDoors,
    AssetEuroNorm,
    AssetFuel,
    AssetGearboxType,
    AssetPartPosition,
    AssetSeatUpholstery,
    AssetTachograph,
    AssetUseType,
    AssetWheelchairRamp,
    BooleanQs,
    type BrandDescriptionEntity,
    BusType,
    ChargingType,
    CruiseControlType,
    DeckType,
    RetarderType,
    RequireScrappingFilter,
    PlatformBusClass,
    SalesListingSortBy,
    SellingMethod,
    SortOrder,
    useApiAttributeValues,
    getApiGetAuctionStatesQueryKey,
    useApiGetAuctionStates,
    VehicleCondition,
} from '~/apiClient';
import { FE_PAGE_PARAM, SLP_ITEMS_PER_PAGE } from '~/constants/pagination';
import { type EnumValue } from '~/types/enum.type';
import { AlpFilterableState, DslpFilterableState, type FilterOptions } from '~/types/filters';
import { type PlatformSellingMethod, SALE_BUNDLE_TYPE_TO_SALE_TYPE_ARRAY_MAP, SaleBundleType } from '~/types/sale.type';

export interface AppliedFilter {
    label: string;
    deleteHandler: () => void;
}

type SlpApiParams<T extends PlatformSellingMethod> = T extends SellingMethod.Auction ? ApiAuctionsListingParams : ApiDirectSalesListingParams;

export const SLP_API_PARAM_KEYS = [
    'itemsPerPage',
    'page',
    'abs',
    'acPassengers',
    'alloyWheels',
    'armRest',
    'asr',
    'axles',
    'brandUrlKeys',
    'breathalyzer',
    'busType',
    'cctv',
    'cdPlayer',
    'certifiedEngineMaintenance',
    'certifiedGearboxMaintenance',
    'chargingType',
    'class',
    'coffeeMachine',
    'coffeeTables',
    'companionSeatsFrom',
    'companionSeatsTo',
    'countryCodes',
    'cruiseControl',
    'curtains',
    'deckType',
    'displacementFrom',
    'displacementTo',
    'doors',
    'doubleDecker',
    'doubleGlazedGlass',
    'driverBerth',
    'dvdPlayer',
    'euroNorm',
    'engineBrand',
    'enginePosition',
    'enginePresent',
    'extendableSeats',
    'externalCameras',
    'firePreventionSystem',
    'firstRegistrationYearFrom',
    'firstRegistrationYearTo',
    'foldingSeatsFrom',
    'foldingSeatsTo',
    'footRest',
    'fuel',
    'fuelTankCapacityFrom',
    'fuelTankCapacityTo',
    'gearboxBrand',
    'gearboxPresent',
    'gearboxType',
    'hatBox',
    'heightFrom',
    'heightTo',
    'indicatorSigns',
    'internalCameras',
    'kitchen',
    'kmFrom',
    'kmTo',
    'powerFrom',
    'powerTo',
    'lengthFrom',
    'lengthTo',
    'microphone',
    'modelUrlKey',
    'monitors',
    'openableWindowsFrom',
    'openableWindowsTo',
    'passThroughLuggageCompartment',
    'preHeater',
    'priceFrom',
    'priceTo',
    'q',
    'radio',
    'recliningSeats',
    'refrigerator',
    'registrationDocumentCountry',
    'requireScrapping',
    'retarder',
    'retrocamera',
    'safetyBelts',
    'saleType',
    'seatPocket',
    'seatsFrom',
    'seatsMonitor',
    'seatsRadio',
    'seatsSocket220',
    'seatsTo',
    'seatsUsbPorts',
    'seatUpholstery',
    'socket220',
    'spareWheel',
    'standingPlacesFrom',
    'standingPlacesTo',
    'state',
    'tachograph',
    'toilet',
    'totalCapacityFrom',
    'totalCapacityTo',
    'useType',
    'vehicleCondition',
    'wheelchairPlacesFrom',
    'wheelchairPlacesTo',
    'wheelchairRamp',
    'wheels',
    'wheelsSizeAxle1',
    'wheelsSizeAxle2',
    'wheelsSizeAxle3',
    'widthFrom',
    'widthTo',
] satisfies (keyof ApiDirectSalesListingParams | keyof ApiAuctionsListingParams)[];

type SortingOption = {
    value: SortingParams<SalesListingSortBy>;
    label: string;
};

export type UseSlpParamsReturn<T extends PlatformSellingMethod> = Pick<UsePaginationParamsReturn, 'page' | 'itemsPerPage'> &
    Pick<UseSortingParamsReturn<SalesListingSortBy>, 'sorting'> & {
        // Filters
        abs: WritableComputedRef<boolean>;
        acPassengers: WritableComputedRef<boolean>;
        alloyWheels: WritableComputedRef<boolean>;
        asr: WritableComputedRef<boolean>;
        axles: WritableComputedRef<number[]>;
        brandIds: WritableComputedRef<string[]>;
        brandUrlKeys: WritableComputedRef<string[]>;
        singleSelectedBrandId: ComputedRef<string | undefined>;
        singleSelectedBrandUrlKey: ComputedRef<string | undefined>;
        brandName: ComputedRef<string | undefined>;
        brandShortDescription: ComputedRef<string | undefined>;
        brandLongDescription: ComputedRef<string | undefined>;
        breathalyzer: WritableComputedRef<boolean>;
        busType: WritableComputedRef<BusType[]>;
        cctv: WritableComputedRef<boolean>;
        cdPlayer: WritableComputedRef<boolean>;
        certifiedEngineMaintenance: WritableComputedRef<boolean>;
        certifiedGearboxMaintenance: WritableComputedRef<boolean>;
        chargingType: ComputedRef<ChargingType[]>;
        coffeeMachine: WritableComputedRef<boolean>;
        coffeeTables: WritableComputedRef<boolean>;
        companionSeatsFrom: WritableComputedRef<number | undefined>;
        companionSeatsTo: WritableComputedRef<number | undefined>;
        countryCodes: WritableComputedRef<CountryISO2[]>;
        cruiseControl: ComputedRef<CruiseControlType[]>;
        curtains: WritableComputedRef<boolean>;
        deckType: WritableComputedRef<DeckType[]>;
        displacementFrom: WritableComputedRef<number | undefined>;
        displacementTo: WritableComputedRef<number | undefined>;
        doors: WritableComputedRef<AssetDoors[]>;
        doubleDecker: WritableComputedRef<boolean>;
        driverBerth: WritableComputedRef<boolean>;
        dvdPlayer: WritableComputedRef<boolean>;
        engineBrand: WritableComputedRef<string[]>;
        enginePosition: WritableComputedRef<AssetPartPosition[]>;
        euroNorm: WritableComputedRef<AssetEuroNorm[]>;
        enginePresent: WritableComputedRef<boolean>;
        extendableSeats: WritableComputedRef<boolean>;
        externalCameras: WritableComputedRef<boolean>;
        firePreventionSystem: WritableComputedRef<boolean>;
        firstRegistrationYearFrom: WritableComputedRef<number | undefined>;
        firstRegistrationYearTo: WritableComputedRef<number | undefined>;
        foldingSeatsFrom: WritableComputedRef<number | undefined>;
        foldingSeatsTo: WritableComputedRef<number | undefined>;
        footRest: WritableComputedRef<boolean>;
        fuel: WritableComputedRef<AssetFuel[]>;
        fuelTankCapacityFrom: WritableComputedRef<number | undefined>;
        fuelTankCapacityTo: WritableComputedRef<number | undefined>;
        gearboxBrand: WritableComputedRef<string[]>;
        gearboxPresent: WritableComputedRef<boolean>;
        gearboxType: WritableComputedRef<AssetGearboxType[]>;
        doubleGlazedGlass: WritableComputedRef<boolean>;
        armRest: WritableComputedRef<boolean>;
        hatBox: WritableComputedRef<boolean>;
        heater: WritableComputedRef<boolean>;
        heightFrom: WritableComputedRef<number | undefined>;
        heightTo: WritableComputedRef<number | undefined>;
        indicatorSigns: WritableComputedRef<boolean>;
        internalCameras: WritableComputedRef<boolean>;
        kitchen: WritableComputedRef<boolean>;
        kmFrom: WritableComputedRef<number | undefined>;
        kmTo: WritableComputedRef<number | undefined>;
        powerFrom: WritableComputedRef<number | undefined>;
        powerTo: WritableComputedRef<number | undefined>;
        lengthFrom: WritableComputedRef<number | undefined>;
        lengthTo: WritableComputedRef<number | undefined>;
        microphone: WritableComputedRef<boolean>;
        modelId: WritableComputedRef<string | undefined>;
        modelName: ComputedRef<string | undefined>;
        selectedModelUrlKey: ComputedRef<string | undefined>;
        monitors: WritableComputedRef<number[]>;
        radio: WritableComputedRef<boolean>;
        openableWindowsFrom: WritableComputedRef<number | undefined>;
        openableWindowsTo: WritableComputedRef<number | undefined>;
        platformBusClass: WritableComputedRef<PlatformBusClass[]>;
        passThroughLuggageCompartment: WritableComputedRef<boolean>;
        preHeater: WritableComputedRef<boolean>;
        priceFrom: WritableComputedRef<number | undefined>;
        priceTo: WritableComputedRef<number | undefined>;
        q: WritableComputedRef<string>;
        recliningSeats: WritableComputedRef<boolean>;
        refrigerator: WritableComputedRef<boolean>;
        registrationDocumentCountry: WritableComputedRef<CountryISO2[]>;
        requireScrapping: WritableComputedRef<RequireScrappingFilter | undefined>;
        retarder: WritableComputedRef<RetarderType[]>;
        retrocamera: WritableComputedRef<boolean>;
        safetyBelts: WritableComputedRef<boolean>;
        saleType: WritableComputedRef<SaleBundleType[]>;
        seatPocket: WritableComputedRef<boolean>;
        seatsFrom: WritableComputedRef<number | undefined>;
        seatsMonitor: WritableComputedRef<boolean>;
        seatsRadio: WritableComputedRef<boolean>;
        seatsSocket220: WritableComputedRef<boolean>;
        seatsTo: WritableComputedRef<number | undefined>;
        seatsUsbPorts: WritableComputedRef<boolean>;
        seatUpholstery: WritableComputedRef<AssetSeatUpholstery[]>;
        socket220: WritableComputedRef<boolean>;
        spareWheel: WritableComputedRef<boolean>;
        standingPlacesFrom: WritableComputedRef<number | undefined>;
        standingPlacesTo: WritableComputedRef<number | undefined>;
        aState: WritableComputedRef<AlpFilterableState[]>;
        dsState: WritableComputedRef<DslpFilterableState[]>;
        tachograph: WritableComputedRef<AssetTachograph[]>;
        toilet: WritableComputedRef<boolean>;
        totalCapacityFrom: WritableComputedRef<number | undefined>;
        totalCapacityTo: WritableComputedRef<number | undefined>;
        useType: WritableComputedRef<AssetUseType[]>;
        vehicleCondition: WritableComputedRef<VehicleCondition[]>;
        wheelchairPlacesFrom: WritableComputedRef<number | undefined>;
        wheelchairPlacesTo: WritableComputedRef<number | undefined>;
        wheelchairRamp: WritableComputedRef<AssetWheelchairRamp[]>;
        wheels: WritableComputedRef<number[]>;
        wheelsSizeAxle1: WritableComputedRef<string[]>;
        wheelsSizeAxle2: WritableComputedRef<string[]>;
        wheelsSizeAxle3: WritableComputedRef<string[]>;
        widthFrom: WritableComputedRef<number | undefined>;
        widthTo: WritableComputedRef<number | undefined>;

        // Options
        axlesOptions: FilterOptions<number>;
        busTypeOptions: FilterOptions<BusType>;
        brandsOptions: FilterOptions<string, { urlKey: string }>;
        chargingTypeOptions: FilterOptions<ChargingType>;
        companionSeatsFromOptions: FilterOptions<number>;
        companionSeatsToOptions: FilterOptions<number>;
        countryCodesOptions: FilterOptions<CountryISO2>;
        cruiseControlOptions: FilterOptions<CruiseControlType>;
        deckTypeOptions: FilterOptions<DeckType>;
        displacementFromOptions: FilterOptions<number>;
        displacementToOptions: FilterOptions<number>;
        doorsOptions: FilterOptions<AssetDoors>;
        engineBrandOptions: FilterOptions<string>;
        enginePositionOptions: FilterOptions<AssetPartPosition>;
        euroNormOptions: FilterOptions<AssetEuroNorm>;
        firstRegistrationYearFromOptions: FilterOptions<number>;
        firstRegistrationYearToOptions: FilterOptions<number>;
        foldingSeatsFromOptions: FilterOptions<number>;
        foldingSeatsToOptions: FilterOptions<number>;
        fuelOptions: FilterOptions<AssetFuel>;
        fuelTankCapacityFromOptions: FilterOptions<number>;
        fuelTankCapacityToOptions: FilterOptions<number>;
        gearboxBrandOptions: FilterOptions<string>;
        gearboxTypeOptions: FilterOptions<AssetGearboxType>;
        heightFromOptions: FilterOptions<number>;
        heightToOptions: FilterOptions<number>;
        kmFromOptions: FilterOptions<number>;
        kmToOptions: FilterOptions<number>;
        powerFromOptions: FilterOptions<number>;
        powerToOptions: FilterOptions<number>;
        lengthFromOptions: FilterOptions<number>;
        lengthToOptions: FilterOptions<number>;
        modelsOptions: FilterOptions<string, { urlKey: string; brandUrlKey?: string }>;
        monitorsOptions: FilterOptions<number>;
        openableWindowsFromOptions: FilterOptions<number>;
        openableWindowsToOptions: FilterOptions<number>;
        platformBusClassOptions: FilterOptions<PlatformBusClass>;
        priceFromOptions: FilterOptions<number>;
        priceToOptions: FilterOptions<number>;
        registrationDocumentCountryOptions: FilterOptions<CountryISO2>;
        requireScrappingOptions: FilterOptions<RequireScrappingFilter>;
        retarderOptions: FilterOptions<RetarderType>;
        saleTypeOptions: FilterOptions<SaleBundleType>;
        seatsFromOptions: FilterOptions<number>;
        seatsToOptions: FilterOptions<number>;
        seatUpholsteryOptions: FilterOptions<AssetSeatUpholstery>;
        standingPlacesFromOptions: FilterOptions<number>;
        standingPlacesToOptions: FilterOptions<number>;
        aStateOptions: FilterOptions<AlpFilterableState>;
        dsStateOptions: FilterOptions<DslpFilterableState>;
        tachographOptions: FilterOptions<AssetTachograph>;
        totalCapacityToOptions: FilterOptions<number>;
        totalCapacityFromOptions: FilterOptions<number>;
        useTypeOptions: FilterOptions<AssetUseType>;
        vehicleConditionOptions: FilterOptions<VehicleCondition>;
        wheelchairPlacesFromOptions: FilterOptions<number>;
        wheelchairPlacesToOptions: FilterOptions<number>;
        wheelchairRampOptions: FilterOptions<AssetWheelchairRamp>;
        wheelsOptions: FilterOptions<number>;
        wheelsSizeAxle1Options: FilterOptions<string>;
        wheelsSizeAxle2Options: FilterOptions<string>;
        wheelsSizeAxle3Options: FilterOptions<string>;
        widthFromOptions: FilterOptions<number>;
        widthToOptions: FilterOptions<number>;

        // SORTING
        sortingOptions: ComputedRef<SortingOption[]>;

        // List of all applied filters
        appliedFilters: ComputedRef<AppliedFilter[]>;
        appliedFiltersCounter: ComputedRef<number>;
        hasAppliedFilters: ComputedRef<boolean>;

        // Utility
        apiParams: ComputedRef<SlpApiParams<T>>;
        reset: () => void;
        suspenseBrands: () => Promise<unknown>;
        suspenseModels: () => Promise<unknown>;
        suspenseBusAssetsAttributeValues: () => Promise<unknown>;
        suspenseAuctionStates: () => Promise<unknown>;
    };

type UpdateRouteParams = {
    abs: BooleanQs.true;
    acPassengers: BooleanQs.true;
    alloyWheels: BooleanQs.true;
    asr: BooleanQs.true;
    axles: number[];
    brandUrlKeys: string[];
    breathalyzer: BooleanQs.true;
    busType: BusType[];
    cctv: BooleanQs.true;
    cdPlayer: BooleanQs.true;
    certifiedEngineMaintenance: BooleanQs.true;
    certifiedGearboxMaintenance: BooleanQs.true;
    chargingType: ChargingType[];
    coffeeMachine: BooleanQs.true;
    coffeeTables: BooleanQs.true;
    companionSeatsFrom: number;
    companionSeatsTo: number;
    countryCodes: CountryISO2[];
    cruiseControl: CruiseControlType[];
    curtains: BooleanQs.true;
    deckType: DeckType[];
    displacementFrom: number;
    displacementTo: number;
    doors: AssetDoors[];
    doubleDecker: BooleanQs.true;
    driverBerth: BooleanQs.true;
    dvdPlayer: BooleanQs.true;
    engineBrand: string[];
    enginePosition: AssetPartPosition[];
    euroNorm: AssetEuroNorm[];
    enginePresent: BooleanQs.true;
    extendableSeats: BooleanQs.true;
    externalCameras: BooleanQs.true;
    firePreventionSystem: BooleanQs.true;
    firstRegistrationYearFrom: number;
    firstRegistrationYearTo: number;
    foldingSeatsFrom: number;
    foldingSeatsTo: number;
    footRest: BooleanQs.true;
    fuel: AssetFuel[];
    fuelTankCapacityFrom: number;
    fuelTankCapacityTo: number;
    gearboxBrand: string[];
    gearboxPresent: BooleanQs.true;
    gearboxType: AssetGearboxType[];
    doubleGlazedGlass: BooleanQs.true;
    armRest: BooleanQs.true;
    hatBox: BooleanQs.true;
    heater: BooleanQs.true;
    heightFrom: number;
    heightTo: number;
    indicatorSigns: BooleanQs.true;
    internalCameras: BooleanQs.true;
    kitchen: BooleanQs.true;
    kmFrom: number;
    kmTo: number;
    powerFrom: number;
    powerTo: number;
    lengthFrom: number;
    lengthTo: number;
    microphone: BooleanQs.true;
    modelUrlKey: string;
    monitors: number[];
    radio: BooleanQs.true;
    openableWindowsFrom: number;
    openableWindowsTo: number;
    platformClass: PlatformBusClass[];
    passThroughLuggageCompartment: BooleanQs.true;
    preHeater: BooleanQs.true;
    priceFrom: number;
    priceTo: number;
    q: string;
    recliningSeats: BooleanQs.true;
    refrigerator: BooleanQs.true;
    registrationDocumentCountry: CountryISO2[];
    requireScrapping: RequireScrappingFilter;
    retarder: RetarderType[];
    retrocamera: BooleanQs.true;
    safetyBelts: BooleanQs.true;
    saleType: SaleBundleType[];
    seatPocket: BooleanQs.true;
    seatsFrom: number;
    seatsMonitor: BooleanQs.true;
    seatsRadio: BooleanQs.true;
    seatsSocket220: BooleanQs.true;
    seatsUsbPorts: BooleanQs.true;
    seatsTo: number;
    seatUpholstery: AssetSeatUpholstery[];
    socket220: BooleanQs.true;
    spareWheel: BooleanQs.true;
    standingPlacesFrom: number;
    standingPlacesTo: number;
    aState: AlpFilterableState[];
    dsState: DslpFilterableState[];
    tachograph: AssetTachograph[];
    toilet: BooleanQs.true;
    totalCapacityFrom: number;
    totalCapacityTo: number;
    useType: AssetUseType[];
    vehicleCondition: VehicleCondition[];
    wheelchairPlacesFrom: number;
    wheelchairPlacesTo: number;
    wheelchairRamp: AssetWheelchairRamp[];
    wheels: number[];
    wheelsSizeAxle1: string[];
    wheelsSizeAxle2: string[];
    wheelsSizeAxle3: string[];
    widthFrom: number;
    widthTo: number;
};

type UpdateRouteParamsKey = keyof UpdateRouteParams;

export type BooleanRouteParamsKey = Extract<
    UpdateRouteParamsKey,
    | 'abs'
    | 'acPassengers'
    | 'alloyWheels'
    | 'armRest'
    | 'asr'
    | 'breathalyzer'
    | 'cctv'
    | 'cdPlayer'
    | 'certifiedEngineMaintenance'
    | 'certifiedGearboxMaintenance'
    | 'coffeeMachine'
    | 'coffeeTables'
    | 'curtains'
    | 'doubleDecker'
    | 'doubleGlazedGlass'
    | 'driverBerth'
    | 'dvdPlayer'
    | 'enginePresent'
    | 'extendableSeats'
    | 'externalCameras'
    | 'firePreventionSystem'
    | 'footRest'
    | 'gearboxPresent'
    | 'hatBox'
    | 'heater'
    | 'indicatorSigns'
    | 'internalCameras'
    | 'kitchen'
    | 'microphone'
    | 'passThroughLuggageCompartment'
    | 'preHeater'
    | 'radio'
    | 'seatsMonitor'
    | 'seatsRadio'
    | 'seatsSocket220'
    | 'seatsUsbPorts'
    | 'socket220'
    | 'recliningSeats'
    | 'refrigerator'
    | 'retrocamera'
    | 'safetyBelts'
    | 'seatPocket'
    | 'spareWheel'
    | 'toilet'
>;

type EnumMultiRouteParamsKey = Extract<
    UpdateRouteParamsKey,
    | 'busType'
    | 'chargingType'
    | 'cruiseControl'
    | 'deckType'
    | 'doors'
    | 'enginePosition'
    | 'euroNorm'
    | 'fuel'
    | 'gearboxType'
    | 'platformClass'
    | 'retarder'
    | 'saleType'
    | 'seatUpholstery'
    | 'tachograph'
    | 'useType'
    | 'vehicleCondition'
    | 'wheelchairRamp'
>;

type NumericFromToRouteParamsKey =
    | 'companionSeats'
    | 'displacement'
    | 'firstRegistrationYear'
    | 'foldingSeats'
    | 'fuelTankCapacity'
    | 'height'
    | 'km'
    | 'length'
    | 'power'
    | 'openableWindows'
    | 'price'
    | 'seats'
    | 'standingPlaces'
    | 'totalCapacity'
    | 'wheelchairPlaces'
    | 'width';

type MultiRouteParamsKey = Extract<
    UpdateRouteParamsKey,
    'axles' | 'engineBrand' | 'gearboxBrand' | 'monitors' | 'wheels' | 'wheelsSizeAxle1' | 'wheelsSizeAxle2' | 'wheelsSizeAxle3'
>;

type CountryISO2MultiRouteParamsKey = Extract<UpdateRouteParamsKey, 'countryCodes' | 'registrationDocumentCountry'>;

export const SLP_QUERY_KEYS: Record<UpdateRouteParamsKey, string> = {
    abs: 'abs',
    acPassengers: 'ac-passengers',
    alloyWheels: 'alloy-wheels',
    asr: 'asr',
    axles: 'axles[]',
    brandUrlKeys: 'brand[]',
    breathalyzer: 'breathalyzer',
    busType: 'type[]',
    cctv: 'cctv',
    cdPlayer: 'cd-player',
    certifiedEngineMaintenance: 'certified-engine-maintenance',
    certifiedGearboxMaintenance: 'certified-geearbox-maintenance',
    chargingType: 'charging-type[]',
    coffeeMachine: 'coffee-machine',
    coffeeTables: 'coffee-tables',
    companionSeatsFrom: 'companion-seats-from',
    companionSeatsTo: 'companion-seats-to',
    countryCodes: 'country-codes[]',
    cruiseControl: 'cruise-control[]',
    curtains: 'curtains',
    deckType: 'deck-type[]',
    displacementFrom: 'displacement-from',
    displacementTo: 'displacement-to',
    doors: 'doors[]',
    doubleDecker: 'double-decker',
    driverBerth: 'driver-berth',
    dvdPlayer: 'dvd-player',
    engineBrand: 'engine-brand[]',
    enginePosition: 'engine-position[]',
    euroNorm: 'euro-norm[]',
    enginePresent: 'engine-present',
    extendableSeats: 'extendable-seats',
    externalCameras: 'external-cameras',
    firePreventionSystem: 'fire-prevention-system',
    firstRegistrationYearFrom: 'year-from',
    firstRegistrationYearTo: 'year-to',
    foldingSeatsFrom: 'folding-seats-from',
    foldingSeatsTo: 'folding-seats-to',
    footRest: 'foot-rest',
    fuel: 'fuel[]',
    fuelTankCapacityFrom: 'fuel-tank-capacity-from',
    fuelTankCapacityTo: 'fuel-tank-capacity-to',
    gearboxBrand: 'gearbox-brand[]',
    gearboxPresent: 'gearbox-present',
    gearboxType: 'gearbox-type[]',
    doubleGlazedGlass: 'double-glazed-glass',
    armRest: 'arm-rest',
    hatBox: 'hat-box',
    heater: 'heater',
    heightFrom: 'height-from',
    heightTo: 'height-to',
    indicatorSigns: 'indicator-signs',
    internalCameras: 'internal-cameras',
    kitchen: 'kitchen',
    kmFrom: 'km-from',
    kmTo: 'km-to',
    powerFrom: 'power-from',
    powerTo: 'power-to',
    lengthFrom: 'length-from',
    lengthTo: 'length-to',
    microphone: 'microphone',
    modelUrlKey: 'model',
    monitors: 'monitors[]',
    radio: 'radio',
    openableWindowsFrom: 'openable-windows-from',
    openableWindowsTo: 'openable-windows-to',
    platformClass: 'class[]',
    passThroughLuggageCompartment: 'pass-through-luggage-compartment',
    preHeater: 'pre-heater',
    priceFrom: 'price-from',
    priceTo: 'price-to',
    q: 'q',
    recliningSeats: 'reclining-seats',
    refrigerator: 'refrigerator',
    registrationDocumentCountry: 'registration-document-country[]',
    requireScrapping: 'require-scrapping',
    retarder: 'retarder[]',
    retrocamera: 'retrocamera',
    safetyBelts: 'safety-belts',
    saleType: 'sale-type[]',
    seatPocket: 'seat-pocket',
    seatsFrom: 'seats-from',
    seatsMonitor: 'seats-monitor',
    seatsRadio: 'seats-radio',
    seatsSocket220: 'seat-socket-220',
    seatsUsbPorts: 'seat-usb-ports',
    seatsTo: 'seats-to',
    seatUpholstery: 'seat-upholstery[]',
    socket220: 'socket-220',
    spareWheel: 'spare-wheel',
    standingPlacesFrom: 'standing-places-from',
    standingPlacesTo: 'standing-places-to',
    aState: 'state[]',
    dsState: 'state[]',
    tachograph: 'tachograph[]',
    toilet: 'toilet',
    totalCapacityFrom: 'total-capacity-from',
    totalCapacityTo: 'total-capacity-to',
    useType: 'use-type[]',
    vehicleCondition: 'vehicle-condition[]',
    wheelchairPlacesFrom: 'wheelchair-places-from',
    wheelchairPlacesTo: 'wheelchair-places-to',
    wheelchairRamp: 'wheelchair-ramp[]',
    wheels: 'wheels[]',
    wheelsSizeAxle1: 'wheels-size-axle1[]',
    wheelsSizeAxle2: 'wheels-size-axle2[]',
    wheelsSizeAxle3: 'wheels-size-axle3[]',
    widthFrom: 'width-from',
    widthTo: 'width-to',
} as const;

export type SlpQueryKey = (typeof SLP_QUERY_KEYS)[keyof typeof SLP_QUERY_KEYS];

const MAP_AUCTION_STATES_TO_ALP_FILTERABLE_STATES: Record<GetAuctionStatesResponseDtoStatesItem, AlpFilterableState> = {
    [GetAuctionStatesResponseDtoStatesItem.Pending]: AlpFilterableState.Pending,
    [GetAuctionStatesResponseDtoStatesItem.Running]: AlpFilterableState.Running,
    [GetAuctionStatesResponseDtoStatesItem.Expired]: AlpFilterableState.Expired,
};

export const useSlpParams = <T extends PlatformSellingMethod>(sellingMethod: MaybeRefOrGetter<T>): UseSlpParamsReturn<T> => {
    // COMPOSABLE
    const route = useRoute();

    const { tt, tte, tn, formatUnits, locale } = useTypedI18n();
    const { page, itemsPerPage, paginationApiParams } = usePaginationParams(SLP_ITEMS_PER_PAGE);
    const { localizedClasses } = useSlpLocalizedRouteParams();
    const parsedRouteParams = useSlpParsedRouteParamsState(route);
    const { trackSlpResetFilters, onSlpFilterChangeByModel, onSlpFilterChangeByValue } = useTracking();
    const { sorting, sortingApiParams } = useSortingParams(SalesListingSortBy);
    const { getLocalizedCountry } = useCountries();
    // N.B. These getters must be defined before brandId/modelId usage, to avoid error "Cannot access 'get...' before initialization"
    const getBrandId = (urlKey: string): string | undefined => brands.value?.brands.find(brand => brand.urlKey === urlKey)?.['id'];
    const getBrandUrlKey = (id: string): string | undefined => brands.value?.brands.find(brand => brand.id === id)?.['urlKey'];
    const getModelId = (urlKey: string): string | undefined => models.value?.models.find(model => model.urlKey === urlKey)?.['id'];
    const getModelUrlKey = (id: string): string | undefined => models.value?.models.find(model => model.id === id)?.['urlKey'];

    // FILTER GENERATION
    const generateBooleanFilter = (key: BooleanRouteParamsKey): [WritableComputedRef<boolean>, ComputedRef<AppliedFilter[]>] => {
        const model = computed<boolean>({
            get: () => locationQueryAsBoolean(route.query[SLP_QUERY_KEYS[key]]),
            set: value => {
                updateRoute({ [key]: value ? BooleanQs.true : undefined });
            },
        });

        const appliedFilter = computed(() => [
            ...insertIf(model.value, {
                label: tt(`slp.filters.${key}`),
                deleteHandler: (): void => {
                    model.value = false;
                },
            }),
        ]);

        return [model, appliedFilter];
    };

    const generateEnumMultiFilter = <EO extends Record<string, string>>(
        key: EnumMultiRouteParamsKey,
        enumObject: EO
    ): [WritableComputedRef<EnumValue<EO>[]>, ComputedRef<AppliedFilter[]>, FilterOptions<EnumValue<EO>>] => {
        const getLabel = (key: EnumMultiRouteParamsKey, value: EnumValue<EO>): string => {
            if (tte(`asset.${key}.${value}`)) {
                return tt(`asset.${key}.${value}` as TranslationKey);
            } else if (tte(`slp.filters.${key}.${value}`)) {
                return tt(`slp.filters.${key}.${value}` as TranslationKey);
            }

            return String(value);
        };

        const options = computed<Array<{ value: EnumValue<EO>; label: string }>>(() =>
            Object.values(enumObject).map(value => {
                const val = value as unknown as EnumValue<EO>;

                return {
                    value: val,
                    label: getLabel(key, val),
                };
            })
        );
        const validValues = getOptionsValidValues<EnumValue<EO>>(options);
        const model = computed({
            get: () => validateMultipleOptionValue<EnumValue<EO>>(route.query[SLP_QUERY_KEYS[key]], validValues.value),
            set: value => {
                onSlpFilterChangeByModel(lodashSnakeCase(key), model.value, value);
                updateRoute({
                    [key]: value,
                });
            },
        });

        const appliedFilter = computed(() => [
            ...model.value.map(_enum => ({
                label: getLabel(key, _enum),
                deleteHandler: (): void => {
                    model.value = model.value.filter(e => e !== _enum);
                },
            })),
        ]);

        return [model, appliedFilter, options];
    };

    const generateNumericFromToFilter = (
        key: NumericFromToRouteParamsKey,
        values: number[],
        labelFormat: (n: number) => string
    ): [
        WritableComputedRef<number | undefined>,
        WritableComputedRef<number | undefined>,
        ComputedRef<AppliedFilter[]>,
        FilterOptions<number>,
        FilterOptions<number>,
    ] => {
        const keyFrom = `${key}From` as const;
        const keyTo = `${key}To` as const;

        const optionsFrom = computed(() => values.map(value => ({ value, label: labelFormat(value) })));
        const optionsTo = computed(() =>
            values
                .filter(value => modelFrom.value === undefined || value >= modelFrom.value)
                .map(value => ({
                    value,
                    label: labelFormat(value),
                }))
        );

        const modelFromValidValues = getOptionsValidValues(optionsFrom);
        const modelToValidValues = getOptionsValidValues(optionsTo);
        const modelFrom = computed<number | undefined>({
            get: () => validateOptionValue(route.query[SLP_QUERY_KEYS[keyFrom]], modelFromValidValues.value),
            set(value) {
                onSlpFilterChangeByValue(lodashSnakeCase(keyFrom), value);
                const shouldResetTo = value && modelTo.value && value > modelTo.value;

                updateRoute({
                    [keyFrom]: value,
                    ...(shouldResetTo && { [keyTo]: undefined }),
                });
            },
        });

        const modelTo = computed<number | undefined>({
            get: () => validateOptionValue(route.query[SLP_QUERY_KEYS[keyTo]], modelToValidValues.value),
            set: value => {
                onSlpFilterChangeByValue(lodashSnakeCase(keyTo), value);
                updateRoute({ [keyTo]: value });
            },
        });

        const appliedFilter = computed(() => [
            ...insertIf(lodashIsNumber(modelFrom.value), {
                label: tt('slp.appliedFilters.from', [
                    tte(`slp.filters.${key}`) ? tt(`slp.filters.${key}` as TranslationKey) : '',
                    labelFormat(modelFrom.value ?? 0),
                ]),
                deleteHandler: (): void => {
                    modelFrom.value = undefined;
                },
            }),
            ...insertIf(lodashIsNumber(modelTo.value), {
                label: tt('slp.appliedFilters.to', [
                    tte(`slp.filters.${key}`) ? tt(`slp.filters.${key}` as TranslationKey) : '',
                    labelFormat(modelTo.value ?? 0),
                ]),
                deleteHandler: (): void => {
                    modelTo.value = undefined;
                },
            }),
        ]);

        return [modelFrom, modelTo, appliedFilter, optionsFrom, optionsTo];
    };

    const generateMultiFilter = <K extends string | number>(
        key: MultiRouteParamsKey,
        values: ComputedRef<K[]>
    ): [WritableComputedRef<K[]>, ComputedRef<AppliedFilter[]>, FilterOptions<K>] => {
        const options = computed<Array<{ value: K; label: string }>>(() =>
            values.value.map(value => ({
                value,
                label: String(value),
            }))
        );
        const validValues = getOptionsValidValues(options);
        const model = computed<K[]>({
            get: () => validateMultipleOptionValue<K>(route.query[SLP_QUERY_KEYS[key]], validValues.value),
            set: value => {
                onSlpFilterChangeByModel(lodashSnakeCase(key), model.value, value);
                updateRoute({
                    [key]: value,
                });
            },
        });

        const appliedFilter = computed(() => [
            ...model.value.map(_value => ({
                label: String(_value),
                deleteHandler: (): void => {
                    model.value = model.value.filter(v => v !== _value);
                },
            })),
        ]);

        return [model, appliedFilter, options];
    };

    const generateCountryISO2MultiFilter = (
        key: CountryISO2MultiRouteParamsKey,
        values: ComputedRef<string[]>
    ): [WritableComputedRef<CountryISO2[]>, ComputedRef<AppliedFilter[]>, FilterOptions<CountryISO2>] => {
        const options = computed(() =>
            values.value.filter(isCountryISO2).map((value: CountryISO2) => ({
                value,
                label: getLocalizedCountry(value) ?? value,
            }))
        );
        const validValues = getOptionsValidValues(options);
        const model = computed({
            get: () => validateMultipleOptionValue(route.query[SLP_QUERY_KEYS[key]], validValues.value),
            set: value => {
                onSlpFilterChangeByModel(lodashSnakeCase(key), model.value, value);
                updateRoute({
                    [key]: value,
                });
            },
        });

        const appliedFilter = computed(() => [
            ...model.value.map(_value => ({
                label: getLocalizedCountry(_value) ?? _value,
                deleteHandler: (): void => {
                    model.value = model.value.filter(v => v !== _value);
                },
            })),
        ]);

        return [model, appliedFilter, options];
    };

    // FILTERS
    const [abs, absAppliedFilters] = generateBooleanFilter('abs');
    const [acPassengers, acPassengersAppliedFilters] = generateBooleanFilter('acPassengers');
    const [alloyWheels, alloyWheelsAppliedFilters] = generateBooleanFilter('alloyWheels');
    const [asr, asrAppliedFilters] = generateBooleanFilter('asr');
    const [axles, axlesAppliedFilters, axlesOptions] = generateMultiFilter(
        'axles',
        computed(() => busAssetAttributeValues.value?.axles ?? [])
    );
    const queryBrandUrlKeys = computed<string[]>(() => {
        const rawUrlKeys = locationQueryAsStringArray(route.query[SLP_QUERY_KEYS.brandUrlKeys]);

        return brandsOptions.value.length ? rawUrlKeys.filter(urlKey => brandsOptions.value.some(option => option.urlKey === urlKey)) : rawUrlKeys;
    });
    const selectedBrandUrlKeys = computed<string[]>(() =>
        lodashUniq([...(parsedRouteParams.value.brandUrlKey ? [parsedRouteParams.value.brandUrlKey] : []), ...queryBrandUrlKeys.value])
    );
    const selectedBrandIds = computed<string[]>(() => selectedBrandUrlKeys.value.map(urlKey => getBrandId(urlKey)).filter(lodashIsString));
    const singleSelectedBrandUrlKey = computed<string | undefined>(() =>
        selectedBrandUrlKeys.value.length === 1 ? selectedBrandUrlKeys.value[0] : undefined
    );
    const singleSelectedBrandId = computed<string | undefined>(() => (singleSelectedBrandUrlKey.value ? selectedBrandIds.value[0] : undefined));
    const brandUrlKeys = computed<string[]>({
        get: () => selectedBrandUrlKeys.value,
        set: value => {
            onSlpFilterChangeByModel('brand', selectedBrandUrlKeys.value, value);
            const keepModel = value.length === 1 && value[0] === singleSelectedBrandUrlKey.value;
            updateRoute({
                brandUrlKeys: value,
                ...(keepModel ? {} : { modelUrlKey: undefined }),
            });
        },
    });
    const brandIds = computed<string[]>({
        get: () => selectedBrandIds.value,
        set: value => {
            brandUrlKeys.value = value.map(id => getBrandUrlKey(id)).filter(lodashIsString);
        },
    });
    const brandName = computed<string | undefined>(
        () => singleSelectedBrandUrlKey.value && brands.value?.brands.find(brand => brand.urlKey === singleSelectedBrandUrlKey.value)?.['brandValue']
    );
    const brandDescription = computed<BrandDescriptionEntity | undefined>(() =>
        singleSelectedBrandUrlKey.value
            ? brands.value?.brands
                  .find(({ urlKey }) => urlKey === singleSelectedBrandUrlKey.value)
                  ?.descriptions.find(({ language }) => lodashIsEqual(language, locale.value))
            : undefined
    );
    const brandShortDescription = computed<string | undefined>(() => brandDescription.value?.shortContent);
    const brandLongDescription = computed<string | undefined>(() => brandDescription.value?.longContent ?? undefined);
    const brandsOptions = computed(() =>
        brands.value && !isPendingBrands.value
            ? brands.value.brands
                  .map(b => ({
                      label: b.brandValue,
                      value: b.id,
                      urlKey: b.urlKey,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))
            : []
    );
    const [breathalyzer, breathalyzerAppliedFilters] = generateBooleanFilter('breathalyzer');
    const [busType, busTypeAppliedFilters, busTypeOptions] = generateEnumMultiFilter('busType', BusType);
    const [cctv, cctvAppliedFilters] = generateBooleanFilter('cctv');
    const [cdPlayer, cdPlayerAppliedFilters] = generateBooleanFilter('cdPlayer');
    const [certifiedEngineMaintenance, certifiedEngineMaintenanceAppliedFilters] = generateBooleanFilter('certifiedEngineMaintenance');
    const [certifiedGearboxMaintenance, certifiedGearboxMaintenanceAppliedFilters] = generateBooleanFilter('certifiedGearboxMaintenance');
    const [chargingType, chargingTypeAppliedFilters, chargingTypeOptions] = generateEnumMultiFilter('chargingType', ChargingType);
    const [coffeeMachine, coffeeMachineAppliedFilters] = generateBooleanFilter('coffeeMachine');
    const [coffeeTables, coffeeTablesAppliedFilters] = generateBooleanFilter('coffeeTables');
    const [companionSeatsFrom, companionSeatsTo, companionSeatsAppliedFilters, companionSeatsFromOptions, companionSeatsToOptions] =
        generateNumericFromToFilter('companionSeats', FILTER_COMPANION_SEATS_VALUES, n => String(n));
    const [countryCodes, countryCodesAppliedFilters, countryCodesOptions] = generateCountryISO2MultiFilter(
        'countryCodes',
        computed(() => busAssetAttributeValues.value?.countryCodes ?? [])
    );
    const [cruiseControl, cruiseControlAppliedFilters, cruiseControlOptions] = generateEnumMultiFilter('cruiseControl', CruiseControlType);
    const [curtains, curtainsAppliedFilters] = generateBooleanFilter('curtains');
    const [displacementFrom, displacementTo, displacementAppliedFilters, displacementFromOptions, displacementToOptions] =
        generateNumericFromToFilter(
            'displacement',
            range(FILTER_DISPLACEMENT_START, FILTER_DISPLACEMENT_END, FILTER_DISPLACEMENT_STEP),
            n => formatUnits.cc(n)!
        );
    const [doors, doorsAppliedFilters, doorsOptions] = generateEnumMultiFilter('doors', AssetDoors);
    const [doubleDecker, doubleDeckerAppliedFilters] = generateBooleanFilter('doubleDecker');
    const [driverBerth, driverBerthAppliedFilters] = generateBooleanFilter('driverBerth');
    const [dvdPlayer, dvdPlayerAppliedFilters] = generateBooleanFilter('dvdPlayer');
    const [deckType, deckTypeAppliedFilters, deckTypeOptions] = generateEnumMultiFilter('deckType', DeckType);
    const [engineBrand, engineBrandAppliedFilters, engineBrandOptions] = generateMultiFilter(
        'engineBrand',
        computed(() => busAssetAttributeValues.value?.engineBrand ?? [])
    );
    const [enginePosition, enginePositionAppliedFilters, enginePositionOptions] = generateEnumMultiFilter('enginePosition', AssetPartPosition);
    const [euroNorm, euroNormAppliedFilters, euroNormOptions] = generateEnumMultiFilter('euroNorm', AssetEuroNorm);
    const [enginePresent, enginePresentAppliedFilters] = generateBooleanFilter('enginePresent');
    const [extendableSeats, extendableSeatsAppliedFilters] = generateBooleanFilter('extendableSeats');
    const [externalCameras, externalCamerasAppliedFilters] = generateBooleanFilter('externalCameras');
    const [firePreventionSystem, firePreventionSystemAppliedFilters] = generateBooleanFilter('firePreventionSystem');
    const [
        firstRegistrationYearFrom,
        firstRegistrationYearTo,
        firstRegistrationYearAppliedFilters,
        firstRegistrationYearFromOptions,
        firstRegistrationYearToOptions,
    ] = generateNumericFromToFilter(
        'firstRegistrationYear',
        range(FILTER_REGISTRATION_YEAR_START, FILTER_REGISTRATION_YEAR_END, FILTER_REGISTRATION_YEAR_STEP),
        n => String(n)
    );
    const [foldingSeatsFrom, foldingSeatsTo, foldingSeatsAppliedFilters, foldingSeatsFromOptions, foldingSeatsToOptions] =
        generateNumericFromToFilter('foldingSeats', FILTER_FOLDING_SEATS_VALUES, n => String(n));
    const [footRest, footRestAppliedFilters] = generateBooleanFilter('footRest');
    const [fuel, fuelAppliedFilters, fuelOptions] = generateEnumMultiFilter('fuel', AssetFuel);
    const [fuelTankCapacityFrom, fuelTankCapacityTo, fuelTankCapacityAppliedFilters, fuelTankCapacityFromOptions, fuelTankCapacityToOptions] =
        generateNumericFromToFilter(
            'fuelTankCapacity',
            range(FILTER_FUEL_TANK_CAPACITY_START, FILTER_FUEL_TANK_CAPACITY_END, FILTER_FUEL_TANK_CAPACITY_STEP, 2),
            n => formatUnits.liters(n)!
        );
    const [gearboxBrand, gearboxBrandAppliedFilters, gearboxBrandOptions] = generateMultiFilter(
        'gearboxBrand',
        computed(() => busAssetAttributeValues.value?.gearboxBrand ?? [])
    );
    const [gearboxPresent, gearboxPresentAppliedFilters] = generateBooleanFilter('gearboxPresent');
    const [gearboxType, gearboxTypeAppliedFilters, gearboxTypeOptions] = generateEnumMultiFilter('gearboxType', AssetGearboxType);
    const [doubleGlazedGlass, doubleGlazedGlassAppliedFilters] = generateBooleanFilter('doubleGlazedGlass');
    const [armRest, armRestAppliedFilters] = generateBooleanFilter('armRest');
    const [hatBox, hatBoxAppliedFilters] = generateBooleanFilter('hatBox');
    const [heater, heaterAppliedFilters] = generateBooleanFilter('heater');
    const [heightFrom, heightTo, heightAppliedFilters, heightFromOptions, heightToOptions] = generateNumericFromToFilter(
        'height',
        range(FILTER_HEIGHT_START, FILTER_HEIGHT_END, FILTER_HEIGHT_STEP, 2),
        n => formatUnits.meters(n)!
    );
    const [indicatorSigns, indicatorSignsAppliedFilters] = generateBooleanFilter('indicatorSigns');
    const [internalCameras, internalCamerasAppliedFilters] = generateBooleanFilter('internalCameras');
    const [kitchen, kitchenAppliedFilters] = generateBooleanFilter('kitchen');
    const [kmFrom, kmTo, kmAppliedFilters, kmFromOptions, kmToOptions] = generateNumericFromToFilter(
        'km',
        range(FILTER_KM_START, FILTER_KM_END, FILTER_KM_STEP),
        n => formatUnits.km(n)!
    );
    const [powerFrom, powerTo, powerAppliedFilters, powerFromOptions, powerToOptions] = generateNumericFromToFilter(
        'power',
        range(FILTER_POWER_START, FILTER_POWER_END, FILTER_POWER_STEP),
        n => formatUnits.kw(n)!
    );
    const [lengthFrom, lengthTo, lengthAppliedFilters, lengthFromOptions, lengthToOptions] = generateNumericFromToFilter(
        'length',
        range(FILTER_LENGTH_START, FILTER_LENGTH_END, FILTER_LENGTH_STEP, 2),
        n => formatUnits.meters(n)!
    );
    const [microphone, microphoneAppliedFilters] = generateBooleanFilter('microphone');
    const selectedModelUrlKey = computed<string | undefined>(() => {
        if (!singleSelectedBrandUrlKey.value) {
            return undefined;
        }

        const queryModelUrlKey = locationQueryAsOptionalString(route.query[SLP_QUERY_KEYS.modelUrlKey]);

        if (queryModelUrlKey) {
            return !modelsOptions.value.length || modelsOptions.value.some(option => option.urlKey === queryModelUrlKey)
                ? queryModelUrlKey
                : undefined;
        }

        return parsedRouteParams.value.modelUrlKey;
    });
    const modelId = computed<string | undefined>({
        get: () => selectedModelUrlKey.value && getModelId(selectedModelUrlKey.value),
        set: value => updateRoute({ modelUrlKey: value && getModelUrlKey(value) }),
    });
    const modelName = computed<string | undefined>(
        () => selectedModelUrlKey.value && models.value?.models.find(model => model.urlKey === selectedModelUrlKey.value)?.['modelValue']
    );
    const modelsOptions = computed(() => {
        if (!singleSelectedBrandId.value || !models.value || isPendingModels.value) {
            return [];
        }

        const brandUrlKey = singleSelectedBrandUrlKey.value;

        return models.value.models
            .map(m => ({
                label: m.modelValue,
                value: m.id,
                urlKey: m.urlKey,
                brandUrlKey,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    });
    const [monitors, monitorsAppliedFilters, monitorsOptions] = generateMultiFilter(
        'monitors',
        computed(() => busAssetAttributeValues.value?.monitors ?? [])
    );
    const [radio, radioAppliedFilters] = generateBooleanFilter('radio');
    const [openableWindowsFrom, openableWindowsTo, openableWindowsAppliedFilters, openableWindowsFromOptions, openableWindowsToOptions] =
        generateNumericFromToFilter(
            'openableWindows',
            range(FILTER_OPENABLE_WINDOWS_START, FILTER_OPENABLE_WINDOWS_END, FILTER_OPENABLE_WINDOWS_STEP),
            n => String(n)
        );
    const platformBusClassOptions = computed(() => Object.values(PlatformBusClass).map(value => ({ value, label: tt(`asset.class.${value}`) })));
    const platformBusClassValidValues = getOptionsValidValues(platformBusClassOptions);
    const platformBusClass = computed<PlatformBusClass[]>({
        // N.B. As for the brand, the path class comes first so that reading back a selection preserves the order it was written in.
        get: () =>
            lodashUniq([
                ...(parsedRouteParams.value.assetType && parsedRouteParams.value.platformClass ? [parsedRouteParams.value.platformClass] : []),
                ...validateMultipleOptionValue(route.query[SLP_QUERY_KEYS.platformClass], platformBusClassValidValues.value),
            ]),
        set: value => {
            onSlpFilterChangeByModel('class', platformBusClass.value, value);
            updateRoute({
                platformClass: value,
            });
        },
    });
    const [passThroughLuggageCompartment, passThroughLuggageCompartmentAppliedFilters] = generateBooleanFilter('passThroughLuggageCompartment');
    const [preHeater, preHeaterAppliedFilters] = generateBooleanFilter('preHeater');
    const [priceFrom, priceTo, priceAppliedFilters, priceFromOptions, priceToOptions] = generateNumericFromToFilter(
        'price',
        range(FILTER_PRICE_START, FILTER_PRICE_END, FILTER_PRICE_STEP),
        n => tn(n, 'currency')
    );
    const [recliningSeats, recliningSeatsAppliedFilters] = generateBooleanFilter('recliningSeats');
    const [refrigerator, refrigeratorAppliedFilters] = generateBooleanFilter('refrigerator');
    const [registrationDocumentCountry, registrationDocumentCountryAppliedFilters, registrationDocumentCountryOptions] =
        generateCountryISO2MultiFilter(
            'registrationDocumentCountry',
            computed(() => busAssetAttributeValues.value?.registrationDocumentCountry ?? [])
        );
    const requireScrapping = computed<RequireScrappingFilter | undefined>({
        get: () => validateOptionValue(route.query[SLP_QUERY_KEYS.requireScrapping], requireScrappingValidValues.value),
        set: value => {
            onSlpFilterChangeByValue('require_scrapping', value);
            updateRoute({ requireScrapping: value });
        },
    });
    const requireScrappingOptions = computed(() =>
        Object.values(RequireScrappingFilter).map(value => ({ value, label: tt(`slp.filters.scrapping.${value}`) }))
    );
    const requireScrappingValidValues = getOptionsValidValues(requireScrappingOptions);
    const [retarder, retarderAppliedFilters, retarderOptions] = generateEnumMultiFilter('retarder', RetarderType);
    const [retrocamera, retrocameraAppliedFilters] = generateBooleanFilter('retrocamera');
    const [safetyBelts, safetyBeltsAppliedFilters] = generateBooleanFilter('safetyBelts');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [saleType, saleTypeAppliedFilters, saleTypeOptions] = generateEnumMultiFilter('saleType', SaleBundleType);
    const [seatPocket, seatPocketAppliedFilters] = generateBooleanFilter('seatPocket');
    const [seatsFrom, seatsTo, seatsAppliedFilters, seatsFromOptions, seatsToOptions] = generateNumericFromToFilter('seats', FILTER_SEATS_VALUES, n =>
        String(n)
    );
    const [seatsMonitor, seatsMonitorAppliedFilters] = generateBooleanFilter('seatsMonitor');
    const [seatsRadio, seatsRadioAppliedFilters] = generateBooleanFilter('seatsRadio');
    const [seatsSocket220, seatsSocket220AppliedFilters] = generateBooleanFilter('seatsSocket220');
    const [seatsUsbPorts, seatsUsbPortsAppliedFilters] = generateBooleanFilter('seatsUsbPorts');
    const [seatUpholstery, seatUpholsteryAppliedFilters, seatUpholsteryOptions] = generateEnumMultiFilter('seatUpholstery', AssetSeatUpholstery);
    const [socket220, socket220AppliedFilters] = generateBooleanFilter('socket220');
    const [spareWheel, spareWheelAppliedFilters] = generateBooleanFilter('spareWheel');
    const [standingPlacesFrom, standingPlacesTo, standingPlacesAppliedFilters, standingPlacesFromOptions, standingPlacesToOptions] =
        generateNumericFromToFilter(
            'standingPlaces',
            range(FILTER_STANDING_PLACES_START, FILTER_STANDING_PLACES_END, FILTER_STANDING_PLACES_STEP),
            n => String(n)
        );
    const aState = computed<AlpFilterableState[]>({
        get: () => validateMultipleOptionValue(route.query[SLP_QUERY_KEYS.aState], aStateValidValues.value),
        set: value => {
            onSlpFilterChangeByModel('state', aState.value, value);
            updateRoute({
                aState: value,
            });
        },
    });
    const aStateOptions = computed(() => {
        const apiStates: AlpFilterableState[] = (auctionStates.value?.states ?? []).map(state => MAP_AUCTION_STATES_TO_ALP_FILTERABLE_STATES[state]);

        return Object.values(AlpFilterableState)
            .filter(s => !apiStates || apiStates.includes(s))
            .map(value => ({
                value: value as AlpFilterableState,
                label: tt(`slp.Auction.filters.state.${value}`),
            }));
    });
    const aStateValidValues = getOptionsValidValues(aStateOptions);
    const dsState = computed<DslpFilterableState[]>({
        get: () => validateMultipleOptionValue(route.query[SLP_QUERY_KEYS.dsState], dsStateValidValues.value),
        set: value => {
            onSlpFilterChangeByModel('state', dsState.value, value);
            updateRoute({
                dsState: value,
            });
        },
    });
    const dsStateOptions = computed(() =>
        Object.values(DslpFilterableState).map(value => ({ value: value as DslpFilterableState, label: tt(`slp.DirectSale.filters.state.${value}`) }))
    );
    const dsStateValidValues = getOptionsValidValues(dsStateOptions);

    const q = computed<string>({
        get: () => {
            const raw = route.query[SLP_QUERY_KEYS.q];
            const value = Array.isArray(raw) ? (raw[0] ?? '') : (raw ?? '');

            // Single source of truth for the min-length contract shared with the
            // backend DTO (@MinLength): below the threshold q acts as if absent,
            // so consumers (apiParams, applied-filter chip, …) all see ''.
            return value.length >= SEARCH_MIN_QUERY_LENGTH ? value : '';
        },
        set: value => {
            updateRoute({ q: value || undefined });
        },
    });
    const qAppliedFilters = computed<AppliedFilter[]>(() =>
        q.value
            ? [
                  {
                      label: tt('slp.appliedFilters.q', [q.value]),
                      deleteHandler: (): void => {
                          q.value = '';
                      },
                  },
              ]
            : []
    );
    const [tachograph, tachographAppliedFilters, tachographOptions] = generateEnumMultiFilter('tachograph', AssetTachograph);
    const [toilet, toiletAppliedFilters] = generateBooleanFilter('toilet');
    const [totalCapacityFrom, totalCapacityTo, totalCapacityAppliedFilters, totalCapacityFromOptions, totalCapacityToOptions] =
        generateNumericFromToFilter('totalCapacity', range(FILTER_TOTAL_CAPACITY_START, FILTER_TOTAL_CAPACITY_END, FILTER_TOTAL_CAPACITY_STEP), n =>
            String(n)
        );
    const [useType, useTypeAppliedFilters, useTypeOptions] = generateEnumMultiFilter('useType', AssetUseType);
    const [vehicleCondition, vehicleConditionAppliedFilters, vehicleConditionOptions] = generateEnumMultiFilter('vehicleCondition', VehicleCondition);
    const [wheelchairPlacesFrom, wheelchairPlacesTo, wheelchairPlacesAppliedFilters, wheelchairPlacesFromOptions, wheelchairPlacesToOptions] =
        generateNumericFromToFilter('wheelchairPlaces', FILTER_WHEELCHAIR_PLACES_VALUES, n => String(n));
    const [wheelchairRamp, wheelchairRampAppliedFilters, wheelchairRampOptions] = generateEnumMultiFilter('wheelchairRamp', AssetWheelchairRamp);
    const [wheels, wheelsAppliedFilters, wheelsOptions] = generateMultiFilter(
        'wheels',
        computed(() => busAssetAttributeValues.value?.wheels ?? [])
    );
    const [wheelsSizeAxle1, wheelsSizeAxle1AppliedFilters, wheelsSizeAxle1Options] = generateMultiFilter(
        'wheelsSizeAxle1',
        computed(() => busAssetAttributeValues.value?.wheelsSizeAxle1 ?? [])
    );
    const [wheelsSizeAxle2, wheelsSizeAxle2AppliedFilters, wheelsSizeAxle2Options] = generateMultiFilter(
        'wheelsSizeAxle2',
        computed(() => busAssetAttributeValues.value?.wheelsSizeAxle2 ?? [])
    );
    const [wheelsSizeAxle3, wheelsSizeAxle3AppliedFilters, wheelsSizeAxle3Options] = generateMultiFilter(
        'wheelsSizeAxle3',
        computed(() => busAssetAttributeValues.value?.wheelsSizeAxle3 ?? [])
    );
    const [widthFrom, widthTo, widthAppliedFilters, widthFromOptions, widthToOptions] = generateNumericFromToFilter(
        'width',
        range(FILTER_WIDTH_START, FILTER_WIDTH_END, FILTER_WIDTH_STEP, 2),
        n => formatUnits.meters(n)!
    );

    // Cross BMV fetch
    const {
        data: brands,
        isPending: isPendingBrands,
        suspense: suspenseBrands,
    } = useQuery({
        queryKey: [sellingMethod, 'brands'],
        queryFn: async () => (toValue(sellingMethod) === SellingMethod.Auction ? apiAuctionsListingBrands() : apiDirectSalesListingBrands()),
    });
    const {
        data: models,
        isPending: isPendingModels,
        suspense: suspenseModels,
    } = useQuery({
        queryKey: [sellingMethod, 'models', singleSelectedBrandId],
        enabled: computed(() => !!unref(singleSelectedBrandId)),
        queryFn: async () =>
            toValue(sellingMethod) === SellingMethod.Auction
                ? apiAuctionsListingModels(singleSelectedBrandId)
                : apiDirectSalesListingModels(singleSelectedBrandId),
    });
    const { data: busAssetAttributeValues, suspense: suspenseBusAssetsAttributeValues } = useApiAttributeValues();
    const { data: auctionStates, suspense: suspenseAuctionStates } = useApiGetAuctionStates({
        query: {
            enabled: computed(() => toValue(sellingMethod) === SellingMethod.Auction),
            queryKey: getApiGetAuctionStatesQueryKey(),
        },
    });

    // SORTING
    const sortingOptions = computed<SortingOption[]>(() => [
        {
            value: { sortBy: SalesListingSortBy.firstRegistrationYear, sortOrder: SortOrder.asc },
            label: tt('slp.sorting.yearAsc'),
        },
        {
            value: { sortBy: SalesListingSortBy.firstRegistrationYear, sortOrder: SortOrder.desc },
            label: tt('slp.sorting.yearDesc'),
        },
        {
            value: { sortBy: SalesListingSortBy.price, sortOrder: SortOrder.asc },
            label: tt('slp.sorting.priceAsc'),
        },
        {
            value: { sortBy: SalesListingSortBy.price, sortOrder: SortOrder.desc },
            label: tt('slp.sorting.priceDesc'),
        },
        {
            value: { sortBy: SalesListingSortBy.km, sortOrder: SortOrder.asc },
            label: tt('slp.sorting.kmAsc'),
        },
        {
            value: { sortBy: SalesListingSortBy.km, sortOrder: SortOrder.desc },
            label: tt('slp.sorting.kmDesc'),
        },
        {
            value: { sortBy: SalesListingSortBy.startAt, sortOrder: SortOrder.asc },
            label: tt('slp.sorting.startAtAsc'),
        },
        {
            value: { sortBy: SalesListingSortBy.startAt, sortOrder: SortOrder.desc },
            label: tt('slp.sorting.startAtDesc'),
        },
    ]);

    // API PARAMS

    const apiParams = computed<SlpApiParams<T>>(() => {
        const state = toValue(sellingMethod) === SellingMethod.Auction ? aState : dsState;

        return {
            abs: toBooleanAPIParam(abs.value),
            acPassengers: toBooleanAPIParam(acPassengers.value),
            alloyWheels: toBooleanAPIParam(alloyWheels.value),
            asr: toBooleanAPIParam(asr.value),
            axles: axles.value,
            brandUrlKeys: selectedBrandUrlKeys.value,
            breathalyzer: toBooleanAPIParam(breathalyzer.value),
            busType: busType.value,
            cctv: toBooleanAPIParam(cctv.value),
            cdPlayer: toBooleanAPIParam(cdPlayer.value),
            certifiedEngineMaintenance: toBooleanAPIParam(certifiedEngineMaintenance.value),
            certifiedGearboxMaintenance: toBooleanAPIParam(certifiedGearboxMaintenance.value),
            chargingType: chargingType.value,
            class: getBusClassesFromPlatformBusClasses(platformBusClass.value),
            coffeeMachine: toBooleanAPIParam(coffeeMachine.value),
            coffeeTables: toBooleanAPIParam(coffeeTables.value),
            companionSeatsFrom: companionSeatsFrom.value,
            companionSeatsTo: companionSeatsTo.value,
            countryCodes: countryCodes.value,
            cruiseControl: cruiseControl.value,
            curtains: toBooleanAPIParam(curtains.value),
            deckType: deckType.value,
            displacementFrom: displacementFrom.value,
            displacementTo: displacementTo.value,
            doors: doors.value,
            doubleDecker: toBooleanAPIParam(doubleDecker.value),
            driverBerth: toBooleanAPIParam(driverBerth.value),
            dvdPlayer: toBooleanAPIParam(dvdPlayer.value),
            engineBrand: engineBrand.value,
            enginePosition: enginePosition.value,
            euroNorm: euroNorm.value,
            enginePresent: toBooleanAPIParam(enginePresent.value),
            extendableSeats: toBooleanAPIParam(extendableSeats.value),
            externalCameras: toBooleanAPIParam(externalCameras.value),
            firePreventionSystem: toBooleanAPIParam(firePreventionSystem.value),
            firstRegistrationYearFrom: firstRegistrationYearFrom.value,
            firstRegistrationYearTo: firstRegistrationYearTo.value,
            foldingSeatsFrom: foldingSeatsFrom.value,
            foldingSeatsTo: foldingSeatsTo.value,
            footRest: toBooleanAPIParam(footRest.value),
            fuel: fuel.value,
            fuelTankCapacityFrom: fuelTankCapacityFrom.value,
            fuelTankCapacityTo: fuelTankCapacityTo.value,
            gearboxBrand: gearboxBrand.value,
            gearboxPresent: toBooleanAPIParam(gearboxPresent.value),
            gearboxType: gearboxType.value,
            doubleGlazedGlass: toBooleanAPIParam(doubleGlazedGlass.value),
            armRest: toBooleanAPIParam(armRest.value),
            hatBox: toBooleanAPIParam(hatBox.value),
            heater: toBooleanAPIParam(heater.value),
            heightFrom: heightFrom.value,
            heightTo: heightTo.value,
            indicatorSigns: toBooleanAPIParam(indicatorSigns.value),
            internalCameras: toBooleanAPIParam(internalCameras.value),
            kitchen: toBooleanAPIParam(kitchen.value),
            kmFrom: kmFrom.value,
            kmTo: kmTo.value,
            powerFrom: powerFrom.value,
            powerTo: powerTo.value,
            lengthFrom: lengthFrom.value,
            lengthTo: lengthTo.value,
            microphone: toBooleanAPIParam(microphone.value),
            modelUrlKey: selectedModelUrlKey.value,
            monitors: monitors.value,
            radio: toBooleanAPIParam(radio.value),
            openableWindowsFrom: openableWindowsFrom.value,
            openableWindowsTo: openableWindowsTo.value,
            passThroughLuggageCompartment: toBooleanAPIParam(passThroughLuggageCompartment.value),
            preHeater: toBooleanAPIParam(preHeater.value),
            priceFrom: priceFrom.value,
            priceTo: priceTo.value,
            q: q.value || undefined,
            recliningSeats: toBooleanAPIParam(recliningSeats.value),
            refrigerator: toBooleanAPIParam(refrigerator.value),
            requireScrapping: requireScrapping.value,
            registrationDocumentCountry: registrationDocumentCountry.value,
            retarder: retarder.value,
            retrocamera: toBooleanAPIParam(retrocamera.value),
            safetyBelts: toBooleanAPIParam(safetyBelts.value),
            saleType: lodashFlatten(saleType.value.map(st => [...SALE_BUNDLE_TYPE_TO_SALE_TYPE_ARRAY_MAP[st]])),
            seatPocket: toBooleanAPIParam(seatPocket.value),
            seatsFrom: seatsFrom.value,
            seatsTo: seatsTo.value,
            seatsMonitor: toBooleanAPIParam(seatsMonitor.value),
            seatsRadio: toBooleanAPIParam(seatsRadio.value),
            seatsSocket220: toBooleanAPIParam(seatsSocket220.value),
            seatsUsbPorts: toBooleanAPIParam(seatsUsbPorts.value),
            seatUpholstery: seatUpholstery.value,
            socket220: toBooleanAPIParam(socket220.value),
            spareWheel: toBooleanAPIParam(spareWheel.value),
            standingPlacesFrom: standingPlacesFrom.value,
            standingPlacesTo: standingPlacesTo.value,
            state: state.value,
            tachograph: tachograph.value,
            toilet: toBooleanAPIParam(toilet.value),
            totalCapacityFrom: totalCapacityFrom.value,
            totalCapacityTo: totalCapacityTo.value,
            useType: useType.value,
            vehicleCondition: vehicleCondition.value,
            wheelchairPlacesFrom: wheelchairPlacesFrom.value,
            wheelchairPlacesTo: wheelchairPlacesTo.value,
            wheelchairRamp: wheelchairRamp.value,
            wheels: wheels.value,
            wheelsSizeAxle1: wheelsSizeAxle1.value,
            wheelsSizeAxle2: wheelsSizeAxle2.value,
            wheelsSizeAxle3: wheelsSizeAxle3.value,
            widthFrom: widthFrom.value,
            widthTo: widthTo.value,
            ...sortingApiParams.value,
            ...paginationApiParams.value,
        } as SlpApiParams<T>;
    });

    // APPLIED FILTERS
    const appliedFilters = computed<AppliedFilter[]>(() => [
        ...absAppliedFilters.value,
        ...acPassengersAppliedFilters.value,
        ...alloyWheelsAppliedFilters.value,
        ...asrAppliedFilters.value,
        ...axlesAppliedFilters.value,
        ...selectedBrandUrlKeys.value.map(_brandUrlKey => ({
            label: brandsOptions.value.find(o => o.urlKey === _brandUrlKey)?.label ?? _brandUrlKey,
            deleteHandler: (): void => {
                brandUrlKeys.value = brandUrlKeys.value.filter(urlKey => urlKey !== _brandUrlKey);
            },
        })),
        ...breathalyzerAppliedFilters.value,
        ...busTypeAppliedFilters.value,
        ...cctvAppliedFilters.value,
        ...cdPlayerAppliedFilters.value,
        ...certifiedEngineMaintenanceAppliedFilters.value,
        ...certifiedGearboxMaintenanceAppliedFilters.value,
        ...chargingTypeAppliedFilters.value,
        ...coffeeMachineAppliedFilters.value,
        ...coffeeTablesAppliedFilters.value,
        ...companionSeatsAppliedFilters.value,
        ...countryCodesAppliedFilters.value,
        ...cruiseControlAppliedFilters.value,
        ...curtainsAppliedFilters.value,
        ...deckTypeAppliedFilters.value,
        ...displacementAppliedFilters.value,
        ...doorsAppliedFilters.value,
        ...doubleDeckerAppliedFilters.value,
        ...driverBerthAppliedFilters.value,
        ...dvdPlayerAppliedFilters.value,
        ...engineBrandAppliedFilters.value,
        ...enginePositionAppliedFilters.value,
        ...euroNormAppliedFilters.value,
        ...enginePresentAppliedFilters.value,
        ...extendableSeatsAppliedFilters.value,
        ...externalCamerasAppliedFilters.value,
        ...firePreventionSystemAppliedFilters.value,
        ...firstRegistrationYearAppliedFilters.value,
        ...foldingSeatsAppliedFilters.value,
        ...footRestAppliedFilters.value,
        ...fuelAppliedFilters.value,
        ...fuelTankCapacityAppliedFilters.value,
        ...gearboxBrandAppliedFilters.value,
        ...gearboxPresentAppliedFilters.value,
        ...gearboxTypeAppliedFilters.value,
        ...doubleGlazedGlassAppliedFilters.value,
        ...armRestAppliedFilters.value,
        ...hatBoxAppliedFilters.value,
        ...heaterAppliedFilters.value,
        ...heightAppliedFilters.value,
        ...indicatorSignsAppliedFilters.value,
        ...internalCamerasAppliedFilters.value,
        ...kitchenAppliedFilters.value,
        ...kmAppliedFilters.value,
        ...powerAppliedFilters.value,
        ...lengthAppliedFilters.value,
        ...microphoneAppliedFilters.value,
        ...insertIf(lodashIsString(selectedModelUrlKey.value), {
            label: modelsOptions.value.find(o => o.urlKey === selectedModelUrlKey.value)?.label ?? selectedModelUrlKey.value!,
            deleteHandler: (): void => {
                modelId.value = undefined;
            },
        }),
        ...monitorsAppliedFilters.value,
        ...radioAppliedFilters.value,
        ...openableWindowsAppliedFilters.value,
        ...platformBusClass.value.map(_platformBusClass => ({
            label: tt(`asset.class.${_platformBusClass}`),
            deleteHandler: (): void => {
                platformBusClass.value = platformBusClass.value.filter(pbc => pbc !== _platformBusClass);
            },
        })),
        ...passThroughLuggageCompartmentAppliedFilters.value,
        ...preHeaterAppliedFilters.value,
        ...priceAppliedFilters.value,
        ...qAppliedFilters.value,
        ...recliningSeatsAppliedFilters.value,
        ...refrigeratorAppliedFilters.value,
        ...insertIf(!!requireScrapping.value, () => ({
            label: tt(`slp.filters.scrapping.${requireScrapping.value!}`),
            deleteHandler: (): void => {
                requireScrapping.value = undefined;
            },
        })),
        ...registrationDocumentCountryAppliedFilters.value,
        ...retarderAppliedFilters.value,
        ...retrocameraAppliedFilters.value,
        ...safetyBeltsAppliedFilters.value,
        ...saleTypeAppliedFilters.value,
        ...seatPocketAppliedFilters.value,
        ...seatsAppliedFilters.value,
        ...seatsMonitorAppliedFilters.value,
        ...seatsRadioAppliedFilters.value,
        ...seatsSocket220AppliedFilters.value,
        ...seatsUsbPortsAppliedFilters.value,
        ...seatUpholsteryAppliedFilters.value,
        ...socket220AppliedFilters.value,
        ...spareWheelAppliedFilters.value,
        ...standingPlacesAppliedFilters.value,
        ...aState.value.map(_state => ({
            label: tt(`slp.Auction.filters.state.${_state}`),
            deleteHandler: (): void => {
                aState.value = aState.value.filter(s => s !== _state);
            },
        })),
        ...dsState.value.map(_state => ({
            label: tt(`slp.DirectSale.filters.state.${_state}`),
            deleteHandler: (): void => {
                dsState.value = dsState.value.filter(s => s !== _state);
            },
        })),
        ...tachographAppliedFilters.value,
        ...toiletAppliedFilters.value,
        ...totalCapacityAppliedFilters.value,
        ...useTypeAppliedFilters.value,
        ...vehicleConditionAppliedFilters.value,
        ...wheelchairPlacesAppliedFilters.value,
        ...wheelchairRampAppliedFilters.value,
        ...wheelsAppliedFilters.value,
        ...wheelsSizeAxle1AppliedFilters.value,
        ...wheelsSizeAxle2AppliedFilters.value,
        ...wheelsSizeAxle3AppliedFilters.value,
        ...widthAppliedFilters.value,
    ]);

    const appliedFiltersCounter = computed<number>(() => appliedFilters.value.length);

    const hasAppliedFilters = computed<boolean>(() => appliedFiltersCounter.value > 0);

    // METHODS
    const navigateToSlp = (optionalParams: string[] | undefined, query: LocationQueryRaw): void => {
        // Navigating by name resolves the params from scratch: omitting the optional catch-all drops it, while a
        // relative navigation would merge it and keep the filters in the url
        navigateTo({ name: route.name, params: { localizedAssetType: route.params.localizedAssetType, optionalParams }, query });
    };

    const reset = (): void => {
        trackSlpResetFilters();
        navigateToSlp(undefined, {});
    };

    const resolveBrandAndModelParams = (
        params: Partial<UpdateRouteParams>
    ): { path: Pick<OptionalParams, 'brandUrlKey' | 'modelUrlKey'>; query: Partial<UpdateRouteParams> } => {
        const nextBrandUrlKeys = Object.hasOwn(params, 'brandUrlKeys') ? (params.brandUrlKeys ?? []) : selectedBrandUrlKeys.value;
        const nextModelUrlKey = Object.hasOwn(params, 'modelUrlKey') ? params.modelUrlKey : selectedModelUrlKey.value;
        const { pathBrandUrlKey, queryBrandUrlKeys: tailBrandUrlKeys } = splitBrandUrlKeys(nextBrandUrlKeys);

        return {
            path: {
                brandUrlKey: pathBrandUrlKey,
                // A model url key is unique only per brand, so it survives only a single-brand selection.
                modelUrlKey: pathBrandUrlKey && !tailBrandUrlKeys ? nextModelUrlKey : undefined,
            },
            query: {
                brandUrlKeys: tailBrandUrlKeys,
                modelUrlKey: undefined,
            },
        };
    };

    const resolveClassParams = (
        params: Partial<UpdateRouteParams>
    ): { path: Pick<OptionalParams, 'localizedClass'>; query: Partial<UpdateRouteParams> } => {
        const nextPlatformClasses = Object.hasOwn(params, 'platformClass') ? (params.platformClass ?? []) : platformBusClass.value;
        const [headPlatformClass, ...tailPlatformClasses] = lodashUniq(nextPlatformClasses);
        const { assetType } = parsedRouteParams.value;

        return {
            path: { localizedClass: headPlatformClass && assetType ? localizedClasses.value[assetType][headPlatformClass] : undefined },
            query: { platformClass: tailPlatformClasses.length ? tailPlatformClasses : undefined },
        };
    };

    const updateRoute = <J extends UpdateRouteParams>(params: Partial<J>): void => {
        const brandAndModel = resolveBrandAndModelParams(params);
        const platformClass = resolveClassParams(params);

        const queryParams = lodashMapKeys(
            {
                ...lodashOmit(params, ['platformClass', 'brandUrlKeys', 'modelUrlKey']),
                ...platformClass.query,
                ...brandAndModel.query,
            },
            (_, key) => SLP_QUERY_KEYS[key as UpdateRouteParamsKey]
        );

        navigateToSlp(formatSlpRouteOptionalParams({ ...platformClass.path, ...brandAndModel.path }), {
            ...route.query,
            [FE_PAGE_PARAM]: undefined,
            ...queryParams,
        });
    };

    return {
        abs,
        acPassengers,
        alloyWheels,
        asr,
        axles,
        brandIds,
        brandUrlKeys,
        singleSelectedBrandId,
        singleSelectedBrandUrlKey,
        brandName,
        brandShortDescription,
        brandLongDescription,
        breathalyzer,
        busType,
        cctv,
        cdPlayer,
        certifiedEngineMaintenance,
        certifiedGearboxMaintenance,
        chargingType,
        coffeeMachine,
        coffeeTables,
        companionSeatsFrom,
        companionSeatsTo,
        countryCodes,
        cruiseControl,
        curtains,
        deckType,
        displacementFrom,
        displacementTo,
        doors,
        doubleDecker,
        driverBerth,
        dvdPlayer,
        engineBrand,
        enginePosition,
        euroNorm,
        extendableSeats,
        enginePresent,
        externalCameras,
        firePreventionSystem,
        firstRegistrationYearFrom,
        firstRegistrationYearTo,
        foldingSeatsFrom,
        foldingSeatsTo,
        footRest,
        fuel,
        fuelTankCapacityFrom,
        fuelTankCapacityTo,
        gearboxBrand,
        gearboxPresent,
        gearboxType,
        doubleGlazedGlass,
        armRest,
        hatBox,
        heater,
        heightFrom,
        heightTo,
        indicatorSigns,
        internalCameras,
        kitchen,
        kmFrom,
        kmTo,
        powerFrom,
        powerTo,
        lengthFrom,
        lengthTo,
        microphone,
        modelId,
        modelName,
        selectedModelUrlKey,
        monitors,
        radio,
        openableWindowsFrom,
        openableWindowsTo,
        platformBusClass,
        passThroughLuggageCompartment,
        preHeater,
        priceFrom,
        priceTo,
        q,
        recliningSeats,
        refrigerator,
        registrationDocumentCountry,
        requireScrapping,
        retarder,
        retrocamera,
        safetyBelts,
        saleType,
        seatPocket,
        seatsFrom,
        seatsMonitor,
        seatsRadio,
        seatsSocket220,
        seatsUsbPorts,
        seatsTo,
        seatUpholstery,
        socket220,
        spareWheel,
        standingPlacesFrom,
        standingPlacesTo,
        aState,
        dsState,
        tachograph,
        toilet,
        totalCapacityFrom,
        totalCapacityTo,
        useType,
        vehicleCondition,
        wheelchairPlacesFrom,
        wheelchairPlacesTo,
        wheelchairRamp,
        wheels,
        wheelsSizeAxle1,
        wheelsSizeAxle2,
        wheelsSizeAxle3,
        widthFrom,
        widthTo,

        sortingOptions,
        sorting,

        page,
        itemsPerPage,

        axlesOptions,
        busTypeOptions,
        brandsOptions,
        chargingTypeOptions,
        companionSeatsFromOptions,
        companionSeatsToOptions,
        countryCodesOptions,
        cruiseControlOptions,
        deckTypeOptions,
        displacementFromOptions,
        displacementToOptions,
        doorsOptions,
        engineBrandOptions,
        enginePositionOptions,
        euroNormOptions,
        firstRegistrationYearFromOptions,
        firstRegistrationYearToOptions,
        foldingSeatsFromOptions,
        foldingSeatsToOptions,
        fuelOptions,
        fuelTankCapacityFromOptions,
        fuelTankCapacityToOptions,
        gearboxBrandOptions,
        gearboxTypeOptions,
        heightFromOptions,
        heightToOptions,
        kmFromOptions,
        kmToOptions,
        powerFromOptions,
        powerToOptions,
        lengthFromOptions,
        lengthToOptions,
        modelsOptions,
        monitorsOptions,
        openableWindowsFromOptions,
        openableWindowsToOptions,
        platformBusClassOptions,
        priceFromOptions,
        priceToOptions,
        registrationDocumentCountryOptions,
        requireScrappingOptions,
        retarderOptions,
        saleTypeOptions,
        seatsFromOptions,
        seatsToOptions,
        seatUpholsteryOptions,
        standingPlacesFromOptions,
        standingPlacesToOptions,
        aStateOptions,
        dsStateOptions,
        tachographOptions,
        totalCapacityFromOptions,
        totalCapacityToOptions,
        useTypeOptions,
        vehicleConditionOptions,
        wheelchairPlacesFromOptions,
        wheelchairPlacesToOptions,
        wheelchairRampOptions,
        wheelsOptions,
        wheelsSizeAxle1Options,
        wheelsSizeAxle2Options,
        wheelsSizeAxle3Options,
        widthFromOptions,
        widthToOptions,

        appliedFilters,
        appliedFiltersCounter,
        hasAppliedFilters,

        apiParams,
        reset,
        suspenseBrands,
        suspenseModels,
        suspenseBusAssetsAttributeValues,
        suspenseAuctionStates,
    };
};
